import CourseInfo from '@/components/elements/course/CourseInfo';
import CourseMaterialsList from '@/components/elements/course/CourseMaterialsList';
import { DEFAULT_AVATAR } from '@/constant/avatar';
import { useScreen } from '@/hooks/useScreen';
import useToggle from '@/hooks/useToggle';
import {
  FolderStatusEnum,
  ICourseDetailResponse,
} from '@/interfaces/training.interface';
import { TextareaAutosize } from '@mui/material';
import { PATH_NAME } from 'configs/pathName';
import { BreadcrumbItem } from 'design-system/components/Breadcrumb';
import { Header } from 'design-system/components/Header';
import { TabDataProps, Tabs } from 'design-system/components/Tabs';
import { Typography } from 'design-system/components/Typography';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useContext, useMemo, useState } from 'react';
import { UseQueryResult } from 'react-query';
import { CourseDetailPermissionContext } from 'state/course/CourseDetailPermissionProvider';

const tabs: TabDataProps[] = [
  {
    title: 'Mô tả',
    tabId: 'overview',
    href: '#overview-section',
  },
  {
    title: 'Chứng chỉ cung cấp',
    tabId: 'materials',
    href: '#materials-section',
  },
  {
    title: 'Nội dung bài học',
    tabId: 'library',
    href: '#library-section',
  },
];

const DynamicSuggestions = dynamic(
  () => import('design-system/components/Cards/Card/Suggestion/Suggestion'),
  { ssr: false }
);
const DynamicCourseSidebar = dynamic(
  () => import('@/components/elements/course/CourseSidebar')
);
const DynamicCourseBottomSheet = dynamic(
  () => import('@/components/elements/course/CourseBottomSheet')
);
const DynamicRequestCertificateModal = dynamic(
  () => import('@/components/modal/training-module/request-certificate'),
  { ssr: false }
);

const CourseDetailInnerRender = () => {
  const router = useRouter();
  const { isMobile } = useScreen();

  const { courseDetailQueryReturn, fullAccess, fullyLock, eachLock } =
    useContext(CourseDetailPermissionContext);
  const { data: course } = courseDetailQueryReturn as UseQueryResult<
    ICourseDetailResponse,
    unknown
  >;

  const { name: folderName, status } = course?.group || {};

  const {
    certificateShortName,
    title,
    courseMaterials,
    requiresVerified,
    description,
    thumbnail,
  } = course || {};

  const [currentTab, setCurrentTab] = useState<number>(0);
  const [openModalRequestCertificate, toggleModalRequestCertificate] =
    useToggle();

  const hasProvideCertificate = certificateShortName;

  const breadcrumb: BreadcrumbItem[] = useMemo(() => {
    if (course?.group) {
      return [
        {
          name: 'Cộng đồng',
          path: PATH_NAME.MY_GROUPS,
          className: 'line-clamp-none whitespace-nowrap',
        },
        {
          name: folderName || '',
          path: {
            pathname: PATH_NAME.MY_GROUPS_DETAIL,
            query: {
              tab: 1,
              gid: course.group.id,
            },
          },
          className: 'max-w-[130px] md:max-w-[300px]',
        },
        {
          name: title || 'Khoá học',
          path: router.asPath,
        },
      ];
    }

    const baseBreadcrumb = [
      {
        name: 'Thư viện Doccen',
        path: PATH_NAME.LIBRARY,
        className: 'line-clamp-none whitespace-nowrap',
      },
      {
        name: title || 'Khoá học',
        path: '#',
        className: 'line-clamp-none whitespace-nowrap',
      },
    ];

    return baseBreadcrumb;
  }, [title]);

  const finalTabs = hasProvideCertificate
    ? tabs
    : tabs.filter((tab) => tab.tabId !== 'materials');

  const hashContent = {
    overview: (
      <TextareaAutosize
        spellCheck={false}
        value={description || 'Chưa có mô tả...'}
        className="pointer-events-none w-full resize-none bg-transparent text-base font-medium leading-[140%] tracking-[-0.6px] text-Text/LightBackground/Secondary focus:outline-none"
      />
    ),
    materials: (
      <div className="flex flex-col items-center justify-center gap-6 md:flex-row md:gap-[64px] md:px-10">
        <DynamicSuggestions
          certificate={course?.certificateShortName || 'CME'}
        />
        <div className="space-y-3 text-center md:text-left">
          <Typography variant="display1" className="text-Primary/Main">
            Chứng chỉ {course?.certificateShortName || 'CME'}
          </Typography>
          <Typography
            variant="body1"
            className="text-Text/LightBackground/Secondary"
          >
            Chứng chỉ đào tạo liên tục về{' '}
            {course?.certificateShortName === 'CME' ? 'Y Khoa' : 'Dược'}
          </Typography>
        </div>
      </div>
    ),
    library: (
      <CourseMaterialsList
        courseMaterials={courseMaterials || []}
        eachLock={eachLock}
        fullyLock={fullyLock}
        fullAccess={fullAccess}
        requiresVerified={requiresVerified || false}
        isPrivateGroup={status === FolderStatusEnum.Private ? true : false}
        allowLessonSkipping={!!course?.allowLessonSkipping}
        isOwner={!!course?.isOwner}
      />
    ),
  };

  return (
    <>
      <div className="flex w-full flex-col items-center">
        <Header
          title=""
          breadcrumb={{
            breadcrumbItems: breadcrumb,
            startPlace: course?.group
              ? {
                  iconName: 'profile-2user',
                  title: 'Cộng đồng',
                }
              : {
                  iconName: 'library',
                  title: 'Thư viện Doccen',
                },
          }}
          className="px-3 py-2 md:px-6 md:py-4"
        />
        <div className="w-full px-0 pb-14 md:px-5">
          <div className="flex w-full max-w-container gap-4">
            <div className="flex h-full w-full flex-1 flex-col items-center bg-white p-4 md:p-6">
              <CourseInfo
                authorName={course?.author.name || ''}
                avatar={course?.author.avatar || DEFAULT_AVATAR}
                linkAuthor={course?.author.profileUrl || ''}
                title={title || ''}
                groupId={course?.group?.id || ''}
                groupName={folderName || ''}
                thumbnail={thumbnail || ''}
                academicRank={course?.author.academicRank || null}
              />

              <Tabs
                tabs={finalTabs}
                selected={currentTab}
                onSelect={(_, index) => setCurrentTab(index)}
                className="sticky left-0 top-[50px] z-[9] mb-6 !w-[calc(100%+32px)] bg-white md:w-[calc(100%+48px)]"
                sx={{
                  '& .MuiTabs-flexContainer': {
                    borderBottom: '2px solid #E5E5E5',
                  },
                }}
              />

              <div className="flex w-full flex-col gap-6 md:gap-[56px]">
                {finalTabs.map((tab) => (
                  <div
                    key={tab.tabId}
                    id={tab.href?.replace('#', '')}
                    className="scroll-mt-28"
                  >
                    <Typography variant="heading2" className="mb-3">
                      {tab.title}
                    </Typography>
                    {hashContent[tab.tabId as keyof typeof hashContent]}
                  </div>
                ))}
              </div>
            </div>

            {isMobile ? (
              <DynamicCourseBottomSheet
                toggleModalRequestCertificate={toggleModalRequestCertificate}
              />
            ) : (
              <DynamicCourseSidebar
                toggleModalRequestCertificate={toggleModalRequestCertificate}
              />
            )}
          </div>
        </div>
      </div>
      {openModalRequestCertificate && (
        <DynamicRequestCertificateModal
          open={openModalRequestCertificate}
          toggle={toggleModalRequestCertificate}
        />
      )}
    </>
  );
};

export default CourseDetailInnerRender;
